import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from "react";

import {
  createBranchQuery,
  deleteBranchById,
  getBranchById,
  updateBranchByIdQuery,
  getAssignedBranchWithoutScopeQuery,
} from "./queries";
import { getBranches } from "../branches/queries";

const BranchContext = createContext({});

export const useBranchContext = () => useContext(BranchContext);

export const BranchProvider = ({ ...props }) => {
  const [branchPerId, setBranchPerId] = useState({});
  const [branchList, setBranchList] = useState([]);
  const [createdBranch, setCreatedBranch] = useState();
  const [staffAssignedBranchWithoutScope, setStaffAssignedBranchWithoutScope] =
    useState([]);

  const getAssignedBranchWithoutScope = useCallback(async (id) => {
    try {
      const result = await getAssignedBranchWithoutScopeQuery(id);
      setStaffAssignedBranchWithoutScope(result);
    } catch (e) {
      console.error("getAssignedBranchWithoutScope", e);
      throw e;
    }
  }, []);

  const branchListGetter = useCallback(async () => {
    try {
      if (branchList?.length === 0) {
        const branches = await getBranches();
        branches.sort((a, b) => a.name.localeCompare(b.name));
        setBranchList(branches);
      }
    } catch (e) {
      console.error("branchListGetter", e);
      throw e;
    }
  }, [branchList]);

  const branchByIdGetter = useCallback(
    async (id) => {
      try {
        setBranchPerId(await getBranchById(id));
      } catch (e) {
        console.error("branchByIdGetter", e);
        throw e;
      }
    },
    [setBranchPerId]
  );

  const branchDeleteById = useCallback(
    async (id) => {
      try {
        await deleteBranchById(id);
        setBranchList((old) => old.filter((row) => row.id !== id));
      } catch (e) {
        console.error("branchDeleteById", e);
        throw e;
      }
    },
    [setBranchList]
  );

  const createBranch = useCallback(
    async (data) => {
      try {
        const branch = await createBranchQuery(data);
        setCreatedBranch(branch);
        setBranchList((old) =>
          [...old, branch].sort((a, b) => a.name.localeCompare(b.name))
        );
      } catch (e) {
        console.error("createBranch", e);
        throw e;
      }
    },
    [setBranchList]
  );

  const updateBranch = useCallback(async (id, data) => {
    try {
      const updatedBranch = await updateBranchByIdQuery(id, data);
      setBranchList((old) =>
        old
          .map((elm) => (elm.id === updatedBranch.id ? updatedBranch : elm))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setBranchPerId(updatedBranch);
    } catch (e) {
      console.error("updateBranch", e);
      throw e;
    }
  }, []);

  const values = useMemo(
    () => ({
      branchListGetter,
      branchByIdGetter,
      branchDeleteById,
      branchList,
      branchPerId,
      setBranchPerId,
      createBranch,
      updateBranch,
      createdBranch,
      setCreatedBranch,
      getAssignedBranchWithoutScope,
      staffAssignedBranchWithoutScope,
    }),
    [
      branchListGetter,
      branchByIdGetter,
      branchDeleteById,
      branchList,
      branchPerId,
      setBranchPerId,
      createBranch,
      updateBranch,
      createdBranch,
      setCreatedBranch,
      getAssignedBranchWithoutScope,
      staffAssignedBranchWithoutScope,
    ]
  );
  return <BranchContext.Provider value={values} {...props} />;
};
